import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { compact, percent, type AnalyticsMetric, type AnalyticsSeries } from "../analytics";
import { DataTable, downloadCsv } from "../components/DataTable";
import { LineChart, type LineSeries } from "../components/LineChart";
import { bucketHeader, bucketLabeller, perWord, RangeControls } from "../components/RangeControls";
import { dayLabel } from "../earlyAccess";
import {
  adopterReads,
  adopterRows,
  countCells,
  countReads,
  countValues,
  FEATURES,
  MAX_COUNTS,
  type Feature,
} from "../features";
import { addDays, latestDay, rangeDays, seriesFor, type Bucket, type RangeId } from "../growth";
import { errorText } from "../problem";
import { readSeries } from "../seriesApi";

const COUNT_TONES = ["series-1", "series-2"] as const;

/**
 * Every read the page shows, together, so a range or bucket change never
 * mixes old and new numbers. Adopters and monthly actives are daily over the
 * last 30 days whatever the range, so a share divides one day's numbers.
 */
function useFeatures(from: string, to: string, bucket: Bucket) {
  return useQuery({
    queryKey: ["analytics-features", from, to, bucket],
    queryFn: async () => {
      const last = latestDay();
      const reads = countReads();
      const own = adopterReads();
      const [users, usersDaily, adopters, active30, ownAdopters, ...counts] = await Promise.all([
        readSeries("feature_users", from, to, bucket, { group: "feature" }),
        // The range's average daily people weigh every day alike (a bucket's mean would not).
        bucket === "day" ? null : readSeries("feature_users", from, to, "day", { group: "feature" }),
        readSeries("feature_adopters", addDays(last, -29), last, "day", { group: "feature" }),
        readSeries("active_30d", addDays(last, -29), last, "day"),
        Promise.all(own.map((metric) => readSeries(metric, addDays(last, -29), last, "day"))),
        ...reads.map((read) => readSeries(read.metric, from, to, bucket, read.group ? { group: read.group } : {})),
      ]);
      const byMetric = new Map<AnalyticsMetric, AnalyticsSeries>(reads.map((read, i) => [read.metric, counts[i]!]));
      const ownByMetric = new Map<AnalyticsMetric, AnalyticsSeries>(own.map((metric, i) => [metric, ownAdopters[i]!]));
      return {
        from,
        to,
        bucket,
        users,
        usersDaily: usersDaily ?? users,
        adopters,
        active30,
        ownAdopters: ownByMetric,
        counts: byMetric,
      };
    },
    placeholderData: keepPreviousData,
    staleTime: 10 * 60_000,
  });
}

export function FeaturesPage() {
  const [range, setRange] = useState<RangeId>("90d");
  const [bucket, setBucket] = useState<Bucket>("week");
  const days = rangeDays(range, bucket, latestDay());
  const query = useFeatures(days.from, days.to, bucket);
  const data = query.data;
  return (
    <>
      <h1>Features</h1>
      <section className="stack">
        <RangeControls range={range} bucket={bucket} onRange={setRange} onBucket={setBucket} />
        {query.isError ? (
          <p className="error">{errorText(query.error)}</p>
        ) : !data ? (
          <p>Loading…</p>
        ) : (
          <>
            <p className="muted">
              UTC days from {dayLabel(data.from)} through {dayLabel(data.to)}
              {data.users.built_through
                ? `; the rollup has built through ${dayLabel(data.users.built_through)}`
                : "; the rollup has built nothing yet"}
              . Counts only: nobody is named.
            </p>
            <Adopters data={data} />
            <Usage data={data} />
          </>
        )}
      </section>
    </>
  );
}

type Data = NonNullable<ReturnType<typeof useFeatures>["data"]>;

const averaged = (value: number) => (Number.isInteger(value) ? value.toLocaleString() : value.toFixed(1));

function Adopters({ data }: { data: Data }) {
  const table = adopterRows({
    adopters: data.adopters,
    active30: data.active30,
    usersDaily: data.usersDaily,
    counts: data.counts,
    ownAdopters: data.ownAdopters,
  });
  const countText = (feature: Feature, counts: (number | null)[]) =>
    feature.counts
      .map((source, i) => `${counts[i] === null ? "—" : compact(counts[i]!)} ${source.label.toLowerCase()}`)
      .join(", ") || "—";
  const header = [
    "Feature",
    "Adopters",
    "Share of monthly actives (%)",
    "Who counts",
    "Average daily people",
    ...Array.from({ length: MAX_COUNTS }, (_, i) => [`Count ${i + 1}`, `Count ${i + 1} in the range`]).flat(),
  ];
  const csvRows = table.rows.map((row) => [
    row.feature.label,
    row.adopters,
    row.share === null ? null : Math.round(row.share * 1000) / 10,
    row.feature.adopters,
    row.daily === null ? null : Math.round(row.daily * 10) / 10,
    ...countCells(row),
  ]);
  return (
    <section className="stack">
      <h2>Adopters</h2>
      <p className="muted">
        {table.day
          ? `As of ${dayLabel(table.day)}, against the ${table.monthlyActive?.toLocaleString() ?? "—"} people active in the 30 days up to then. `
          : "No snapshot in the last 30 days: the rollup counts adopters only for the latest day it builds. "}
        Average daily people and the counts cover the range above.
      </p>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Feature</th>
              <th className="num">Adopters</th>
              <th>Share of monthly actives</th>
              <th>Who counts</th>
              <th className="num">Average daily people</th>
              <th>In the range</th>
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row) => (
              <tr key={row.feature.id}>
                <td>{row.feature.label}</td>
                <td className="num">{row.adopters === null ? "—" : row.adopters.toLocaleString()}</td>
                <td>
                  <span className="meter" aria-hidden="true">
                    <span style={{ width: `${Math.min(1, row.share ?? 0) * 100}%` }} />
                  </span>{" "}
                  {percent(row.share)}
                </td>
                <td className="muted">{row.feature.adopters}</td>
                <td className="num">{row.daily === null ? "—" : averaged(row.daily)}</td>
                <td>{countText(row.feature, row.counts)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        <button
          type="button"
          className="button-quiet"
          onClick={() => downloadCsv(`ten20-adopters-${table.day ?? "none"}.csv`, header, csvRows)}
        >
          Download CSV
        </button>
      </p>
    </section>
  );
}

function Usage({ data }: { data: Data }) {
  const per = perWord(data.bucket);
  const bucketLabel = bucketLabeller(data.bucket);
  const buckets = data.users.buckets;
  const people = data.bucket === "day" ? "People" : "People per day (average)";
  const charts = FEATURES.map((feature) => {
    const series: LineSeries[] = feature.counts.map((source, i) => ({
      key: source.label,
      label: source.label,
      tone: COUNT_TONES[i]!,
      values: countValues(source, data.counts.get(source.metric)),
    }));
    if (feature.daily)
      series.push({
        key: "people",
        label: people,
        tone: "muted",
        values: seriesFor(data.users, "feature", feature.id),
      });
    return { feature, series };
  }).filter(({ series }) => series.length > 0);
  return (
    <section className="stack">
      <h2>Use per {per}</h2>
      <div className="charts">
        {charts.map(({ feature, series }) => (
          <LineChart
            key={feature.id}
            title={feature.label}
            buckets={buckets}
            bucketLabel={bucketLabel}
            format={averaged}
            series={series}
          />
        ))}
      </div>
      <DataTable
        header={[
          bucketHeader(data.bucket),
          ...charts.flatMap(({ feature, series }) => series.map((s) => `${feature.label}: ${s.label}`)),
        ]}
        rows={buckets
          .map((start, i) => [
            start,
            ...charts.flatMap(({ series }) =>
              series.map((s) => {
                const v = s.values[i];
                return v === null || v === undefined ? null : Math.round(v * 10) / 10;
              }),
            ),
          ])
          .toReversed()}
        fileName={`ten20-features-${data.from}-${data.to}-${data.bucket}.csv`}
        format={averaged}
      />
    </section>
  );
}
